/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {omitThemingProps, Stack, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {useItemVariant} from '.'
import {useCurrency} from '../../hooks'

import {getDisplayPrice} from '../../utils/product-utils'
import DisplayPrice from '../display-price'
import {calculateNetPrice} from '../../utils/utils'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

const PricePerItem = ({currency, basket, basePrice}) => {
    const {currency: activeCurrency} = useCurrency()
    return (
        <Text fontSize={{base: '12px', lg: '14px'}}>
            <FormattedNumber
                style="currency"
                currency={currency || basket?.currency || activeCurrency}
                value={basePrice}
            />
            <FormattedMessage
                defaultMessage="Price per item"
                id="price_per_item.label.each"
                description="Abbreviated 'each', follows price per item, like $10/ea"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string,
    basket: PropTypes.object,
    basePrice: PropTypes.string
}

/**
 * In the context of a cart product item variant, this component renders the item's
 * pricing, taking into account applied discounts as well as base item prices.
 */
const ItemPrice = (props) => {
    const variant = useItemVariant()
    const {data: basket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()
    const {currency, productLineItem, ...ownProps} = omitThemingProps(props)
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data

    const {price, displayPrice, hasDiscount, bonusProductLineItem, discountPercentage} =
        useMemo(() => {
            const isProductASet = variant?.type?.set
            let price
            let displayPrice
            let hasDiscount
            let bonusProductLineItem = false
            if (isProductASet) {
                const psPrices = getDisplayPrice(variant, false, isCustomNetPriceEnabled)
                price = psPrices.price
                displayPrice = psPrices.displayPrice
                hasDiscount = psPrices.hasDiscount
            } else {
                const correspondingPLI =
                    productLineItem ||
                    basket?.productItems?.find((item) => item.productId === variant.id)
                price = correspondingPLI?.price || variant.price
                displayPrice =
                    correspondingPLI?.priceAfterItemDiscount ||
                    correspondingPLI?.priceAfterItemDiscount === 0
                        ? Math.min(price, correspondingPLI?.priceAfterItemDiscount)
                        : price
                if (isCustomNetPriceEnabled) {
                    price = calculateNetPrice(
                        price,
                        correspondingPLI?.taxRate ||
                            correspondingPLI?.c_taxRate ||
                            variant.c_taxRate
                    )
                    displayPrice = calculateNetPrice(
                        displayPrice,
                        correspondingPLI?.taxRate ||
                            correspondingPLI?.c_taxRate ||
                            variant.c_taxRate
                    )
                }

                hasDiscount = displayPrice !== price
                bonusProductLineItem = correspondingPLI?.bonusProductLineItem
            }

            // invalid rounding with floor. Here is an example:
            // Exactly 5% -> (100 * (239- 227.05)) / 239
            // result is 4.999999999999996 and after using
            // floor the result is 4%, but the round returns 5%
            const discountPercentage = Math.round((100 * (price - displayPrice)) / price)

            return {price, displayPrice, hasDiscount, bonusProductLineItem, discountPercentage}
        }, [variant, isCustomNetPriceEnabled, productLineItem, basket?.productItems])

    const styles = useMultiStyleConfig('ItemVariantPrice', {...props, hasDiscount})

    return (
        <Stack {...styles.container} {...ownProps}>
            {bonusProductLineItem ? (
                <FormattedMessage
                    defaultMessage="Bonus Product"
                    id="item_attributes.label.bonus_product"
                />
            ) : (
                <DisplayPrice
                    price={price}
                    displayPrice={displayPrice}
                    hasDiscount={hasDiscount}
                    activeCurrency={activeCurrency}
                    variant={props.variant}
                    discountPercentage={discountPercentage}
                />
            )}
        </Stack>
    )
}

ItemPrice.propTypes = {
    productLineItem: PropTypes.object,
    currency: PropTypes.string,
    variant: PropTypes.oneOf(['cart', 'miniCart', 'pdp', 'order', 'tile', 'orderHistory'])
}

export default ItemPrice
